"use client";

import { Formik, Form } from "formik";
import { useEffect, useState } from "react";
import { FaExternalLinkAlt } from "react-icons/fa";
import { GoCheckCircleFill } from "react-icons/go";
import { motion, AnimatePresence } from "framer-motion";
import PrimaryInput from "src/components/atoms/PrimaryInput";
import PrimaryButton from "src/components/atoms/PrimaryButton";
import { IoCloseCircleSharp, IoCloudUploadOutline } from "react-icons/io5";
import { initialValuesVacancy, validationSchemaVacancy } from "src/utils/mock/vacancyFromValidation";

const ApplyFormModal = ({ vacancy, onClose }) => {
  useEffect(() => {
    const scrollY = window.scrollY;

    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";

      window.scrollTo(0, scrollY);
    };
  }, []);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setSubmitError("");
    try {
      const formData = new FormData();
      formData.append("resume", values.resume);

      const uploadRes = await fetch("https://api-tycho.digitalseller.in/upload/resume", {
        method: "POST",
        body: formData,
      });

      if (!uploadRes.ok) {
        throw new Error("Resume upload failed. Please try again.");
      }

      const uploadData = await uploadRes.json();
      const resumeUrl = uploadData?.url || uploadData?.data?.url;

      if (!resumeUrl) {
        throw new Error("Could not get uploaded resume URL.");
      }

      const applyRes = await fetch("https://api-tycho.digitalseller.in/contact-us/apply-student", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fullName: values.fullName,
          email: values.email,
          phone: values.phone,
          experience: values.experience,
          currentCTC: values.currentCTC,
          expectedCTC: values.expectedCTC,
          resume: resumeUrl,
          vacancyId: vacancy?.id,
        }),
      });

      if (!applyRes.ok) {
        throw new Error("Application submission failed. Please try again.");
      }

      setIsSubmitted(true);
    } catch (err) {
      console.error(err);
      setSubmitError(err.message || "Something went wrong. Please try again.");
      setFieldError("resume", err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10001] flex items-center justify-center !p-4 bg-black/60 backdrop-blur-md"
      >
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.96 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-6xl bg-[var(--c-surface)] border border-[var(--c-border)] rounded-3xl overflow-hidden shadow-2xl max-h-[88vh] flex flex-col"
        >
          <div className="flex justify-between items-start !p-8 border-b border-[var(--c-border)] flex-shrink-0">
            <div>
              <h3 className="text-2xl font-bold text-[var(--c-ink)]">Apply For</h3>
              <p className="text-violet-400 font-medium !mt-1">{vacancy.title}</p>
            </div>

            <IoCloseCircleSharp onClick={onClose} className="text-[var(--c-ink-2)] hover:text-[var(--c-ink)] transition-colors cursor-pointer" size={35} />
          </div>

          <AnimatePresence mode="wait">
            {!isSubmitted ? (
              <div className="flex-1 overflow-y-auto !p-8 modern-scroll">
                <Formik initialValues={initialValuesVacancy} validationSchema={validationSchemaVacancy} onSubmit={handleSubmit}>
                  {({ isSubmitting, setFieldValue, values, errors, touched }) => (
                    <Form className="!space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 !gap-6">
                        <PrimaryInput label="Full Name" name="fullName" required placeholder="john singh" />
                        <PrimaryInput label="Email Address" name="email" type="email" required placeholder="xyz@gmail.com" />
                        <PrimaryInput label="Phone Number" name="phone" type="tel" required placeholder="1234567890" />
                        <PrimaryInput label="Years of Experience" name="experience" placeholder="4.5 years" required />
                        <PrimaryInput label="Current CTC" name="currentCTC" placeholder="12 LPA" required />
                        <PrimaryInput label="Expected CTC" name="expectedCTC" placeholder="18 LPA" required />
                      </div>

                      <div className="group relative overflow-hidden rounded-xl border border-[var(--c-border)] bg-[var(--c-surface2)] backdrop-blur-xl transition-all duration-500 hover:border-violet-500/40 hover:bg-[var(--c-border)] !mt-5">
                        <input
                          type="file"
                          accept=".pdf,application/pdf"
                          className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                          onChange={(event) => {
                            const file = event.currentTarget.files[0];
                            setFieldValue("resume", file);
                          }}
                        />

                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(139,92,246,0.18),transparent_60%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                        <div className="relative flex flex-col items-center justify-center !px-6 !py-4 text-center">
                          <div className="!mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,var(--c-brand,#3b5bdb)_0%,var(--c-accent,#00b4d8)_100%)] shadow-[0_10px_40px_rgba(59,91,219,0.35)] transition-transform duration-500 group-hover:scale-110">
                            <IoCloudUploadOutline size={28} className="text-white" />
                          </div>

                          <h3 className="text-xl font-semibold text-[var(--c-ink)]">Upload Resume</h3>

                          <p className="!mt-2 max-w-md text-sm leading-relaxed text-[var(--c-ink-2)]">Drag & drop your PDF here or click to browse.</p>

                          {values.resume && <p className="!mt-4 text-sm text-emerald-400 font-medium">{values.resume.name}</p>}

                          {errors.resume && touched.resume && <p className="!mt-2 text-sm text-red-400">{errors.resume}</p>}
                        </div>
                      </div>

                      {submitError && <p className="text-center text-sm text-red-400 !mt-2">{submitError}</p>}

                      <div className="flex justify-center ">
                        <PrimaryButton size="lg" icon={<FaExternalLinkAlt />} className="!px-5 !py-2 !mt-8" loading={isSubmitting}>
                          Submit Application
                        </PrimaryButton>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            ) : (
              <div className="text-center !p-12 flex flex-col justify-center items-center min-h-[400px]">
                <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", bounce: 0.4 }}>
                  <GoCheckCircleFill className="w-24 h-24 text-emerald-500 mx-auto !mb-6" />
                </motion.div>

                <h2 className="text-4xl font-bold text-[var(--c-ink)] !mb-3">Thank You!</h2>
                <p className="text-[var(--c-ink-2)] text-lg !mb-4">
                  Your application for <span className="text-violet-400 font-medium">{vacancy.title}</span> has been received.
                </p>
                <p className="text-[var(--c-ink-2)] max-w-md !mb-10">Our team will review your application and get back to you within 3–5 business days.</p>

                <button
                  onClick={onClose}
                  className="px-10! !py-4 rounded-2xl border border-[var(--c-border)] hover:bg-[var(--c-surface2)] text-[var(--c-ink)] font-medium transition-all"
                >
                  Close Window
                </button>
              </div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ApplyFormModal;
